import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import type { LibraryItem, LibraryStatus } from '../types'
import { friendlyDataError } from '../lib/dataErrors'

type LibraryContextValue = {
  items: LibraryItem[]
  loading: boolean
  message: string
  clearMessage: () => void
  itemFor: (bookId: string) => LibraryItem | undefined
  setStatus: (bookId: string, status: LibraryStatus) => Promise<void>
  setProgress: (bookId: string, progress: number) => Promise<void>
  toggleWantToRead: (bookId: string) => Promise<void>
}

const demoSeed: LibraryItem[] = [
  { bookId: 'dom-casmurro', status: 'reading', progress: 28 },
  { bookId: 'o-cortico', status: 'reading', progress: 9 },
  { bookId: 'iracema', status: 'want_to_read', progress: 0 },
  { bookId: 'memorias-postumas', status: 'want_to_read', progress: 0 },
  { bookId: 'helena', status: 'want_to_read', progress: 0 },
  { bookId: 'policarpo-quaresma', status: 'completed', progress: 100 }
]

const LibraryContext = createContext<LibraryContextValue | null>(null)
const DEMO_STORAGE_KEY = 'livroom-library-demo-v3'

function readDemo() {
  try {
    const raw = localStorage.getItem(DEMO_STORAGE_KEY)
    return raw ? JSON.parse(raw) as LibraryItem[] : demoSeed
  } catch { return demoSeed }
}

export function LibraryProvider({ children }: { children: ReactNode }) {
  const { user, demo } = useAuth()
  const [items, setItems] = useState<LibraryItem[]>(() => demo && !user ? readDemo() : [])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (user && supabase) {
      let active = true
      setLoading(true)
      supabase.from('library_items').select('book_id,status,progress').eq('user_id', user.id).then(({ data, error }) => {
        if (!active) return
        if (!error) setItems((data || []).map((row) => ({ bookId: row.book_id, status: row.status as LibraryStatus, progress: row.progress ?? 0 })))
        else setMessage(friendlyDataError(error, 'carregar sua biblioteca'))
        setLoading(false)
      })
      return () => { active = false }
    }
    setLoading(false)
    setItems(demo ? readDemo() : [])
  }, [user, demo])

  useEffect(() => {
    if (demo && !user) localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(items))
  }, [demo, items, user])

  async function persist(next: LibraryItem) {
    if (!user || !supabase) return true
    const { error } = await supabase.from('library_items').upsert({
      user_id: user.id,
      book_id: next.bookId,
      status: next.status,
      progress: next.progress,
      updated_at: new Date().toISOString()
    }, { onConflict: 'user_id,book_id' })
    if (error) setMessage(friendlyDataError(error, 'sincronizar sua biblioteca'))
    else setMessage('')
    return !error
  }

  async function setStatus(bookId: string, status: LibraryStatus) {
    const before = items
    const current = items.find((item) => item.bookId === bookId)
    const next: LibraryItem = { bookId, status, progress: status === 'completed' ? 100 : current?.progress ?? 0 }
    setItems((old) => [...old.filter((item) => item.bookId !== bookId), next])
    if (!await persist(next)) setItems(before)
  }

  async function setProgress(bookId: string, progress: number) {
    const before = items
    const safe = Math.max(0, Math.min(100, Math.round(progress)))
    const current = items.find((item) => item.bookId === bookId)
    const next: LibraryItem = { bookId, status: safe >= 100 ? 'completed' : 'reading', progress: safe }
    if (current?.status === 'want_to_read' && safe === 0) next.status = 'want_to_read'
    setItems((old) => [...old.filter((item) => item.bookId !== bookId), next])
    if (!await persist(next)) setItems(before)
  }

  async function toggleWantToRead(bookId: string) {
    const before = items
    const current = items.find((item) => item.bookId === bookId)
    if (current?.status === 'reading' || current?.status === 'completed') return
    if (current?.status === 'want_to_read') {
      setItems((old) => old.filter((item) => item.bookId !== bookId))
      if (user && supabase) {
        const { error } = await supabase.from('library_items').delete().eq('user_id', user.id).eq('book_id', bookId)
        if (error) { setItems(before); setMessage(friendlyDataError(error, 'remover o livro da sua lista')) }
      }
      return
    }
    await setStatus(bookId, 'want_to_read')
  }

  const value = useMemo<LibraryContextValue>(() => ({
    items,
    loading,
    message,
    clearMessage: () => setMessage(''),
    itemFor: (bookId) => items.find((item) => item.bookId === bookId),
    setStatus,
    setProgress,
    toggleWantToRead
  }), [items, loading, message, user, demo])

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>
}

export function useLibrary() {
  const value = useContext(LibraryContext)
  if (!value) throw new Error('useLibrary must be used inside LibraryProvider')
  return value
}
